// Wall-clock handling for ThunderTix performance times.
//
// ThunderTix returns ISO strings such as "2026-10-24T19:00:00.000-05:00".
// The wall-clock portion (Oct 24, 7:00 PM) is the tour's local start time.
// The offset has historically been Central-labelled even though tours run on
// Eastern time, so we never convert through the visitor's timezone: we show
// exactly what is written and compare against "now" in the venue's timezone.

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const VENUE_TIME_ZONE = "America/New_York";

/** Parse the calendar date and wall-clock time out of an ISO string, ignoring its offset. */
export function parseWallClock(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || "");
  if (!m) return null;
  const wc = { year: +m[1], month: +m[2], day: +m[3], hour: +m[4], minute: +m[5] };
  if (wc.month < 1 || wc.month > 12 || wc.day < 1 || wc.day > 31 || wc.hour > 23 || wc.minute > 59) {
    return null;
  }
  return wc;
}

/** Day name for a calendar date; uses UTC arithmetic so the visitor's zone can't shift it. */
export function dayOfWeek(wc) {
  return DAYS[new Date(Date.UTC(wc.year, wc.month - 1, wc.day)).getUTCDay()];
}

/** Sortable integer YYYYMMDDHHMM. */
export function wallClockKey(wc) {
  return ((wc.year * 100 + wc.month) * 100 + wc.day) * 10000 + wc.hour * 100 + wc.minute;
}

/** The current wall-clock time in the venue's timezone, as a parseWallClock-shaped object. */
export function nowInVenueTimeZone(date) {
  const d = date || new Date();
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: VENUE_TIME_ZONE,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).formatToParts(d);
    const get = (type) => Number(parts.find((p) => p.type === type).value);
    return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour") % 24, minute: get("minute") };
  } catch (e) {
    return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate(), hour: d.getHours(), minute: d.getMinutes() };
  }
}

/** Display fields for one performance time. Returns null for unparseable input. */
export function formatPerformance(iso) {
  const wc = parseWallClock(iso);
  if (!wc) return null;
  const hour12 = wc.hour % 12 === 0 ? 12 : wc.hour % 12;
  const minutes = wc.minute < 10 ? "0" + wc.minute : String(wc.minute);
  return {
    dayofweek: dayOfWeek(wc),
    month: MONTHS[wc.month - 1],
    day: String(wc.day),
    year: String(wc.year),
    time: hour12 + ":" + minutes,
    ampm: wc.hour < 12 ? "AM" : "PM",
    short: wc.month + "/" + wc.day + "/" + String(wc.year).slice(2),
    long: iso,
    sortKey: wallClockKey(wc),
  };
}

/** True when the performance starts after "now" (venue wall-clock). */
export function isUpcoming(iso, now) {
  const wc = parseWallClock(iso);
  if (!wc) return false;
  return wallClockKey(wc) > wallClockKey(now || nowInVenueTimeZone());
}
